import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, MapPin, Star, ArrowRight, ArrowUpRight, Check, ShieldCheck, Clock, PoundSterling, Phone, BadgeCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Layout } from "@/components/Layout";
import { FleetCarousel } from "@/components/FleetCarousel";
import { AppShowcase, StoreButtons } from "@/components/AppShowcase";
import { HeroBackground } from "@/components/HeroBackground";
import { ServicesShowcase } from "@/components/ServicesShowcase";
import { StoryShowcase } from "@/components/StoryShowcase";
import { FilmSection } from "@/components/FilmSection";
import { GatwickLiveWait } from "@/components/GatwickLiveWait";
import { AREAS, TESTIMONIALS, STATS, HOW_IT_WORKS, FAQ, WHY_CHOOSE, ACCREDITATIONS, PATRONS, COMPANY, BOOKING_URL } from "@/data/site";
import { Seo } from "@/components/Seo";
import { getArticles } from "@/lib/api";

const iconMap = { Plane, MapPin };

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const Eyebrow = ({ children, dark }) => (
  <p className={`text-xs font-bold uppercase tracking-[0.2em] mb-4 ${dark ? "text-brand-light" : "text-brand"}`}>{children}</p>
);

export default function Home() {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    getArticles().then((a) => setArticles(a || [])).catch(() => setArticles([]));
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${origin}/#localbusiness`,
    name: COMPANY.name,
    url: `${origin}/`,
    telephone: COMPANY.phoneRaw,
    email: COMPANY.email,
    image: `${origin}/images/clocktower-logo.png`,
    priceRange: "££",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Churchill Court",
      addressLocality: "Crawley",
      postalCode: "RH10 9KU",
      addressCountry: "GB",
    },
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    }],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: String(COMPANY.reviews || "774"),
      bestRating: "5",
      worstRating: "1",
    },
  };

  return (
    <Layout>
      <Seo path="/" jsonLd={[orgJsonLd, faqJsonLd]} />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <HeroBackground />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/75 to-ink/45" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center pt-24 pb-20 lg:py-28">
          <div className="lg:col-span-6">
            <motion.div {...fadeUp} className="inline-flex items-center gap-2 bg-white/10 ring-1 ring-white/20 backdrop-blur rounded-full pl-2 pr-4 py-1.5 mb-7">
              <span className="flex items-center gap-0.5 text-brand-light">{[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-brand-light" />)}</span>
              <span className="text-sm font-semibold text-white">4.9/5 from 2,000+ riders</span>
            </motion.div>
            <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.92] text-white">
              Luxury airport transfers,<br /><span className="text-brand-light">perfectly timed.</span>
            </motion.h1>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-6 text-lg text-white/75 max-w-lg leading-relaxed">
              {COMPANY.welcome}
            </motion.p>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="mt-8 flex flex-wrap items-center gap-4">
              <a href={BOOKING_URL} data-testid="hero-book-btn" className="bg-brand hover:bg-brand-light text-white px-8 py-4 rounded-full font-bold transition-all duration-300 hover:shadow-[0_8px_24px_rgba(31,157,42,0.4)] active:scale-95">
                Book your journey
              </a>
              <Link to="/fleet" data-testid="hero-fleet-btn" className="inline-flex items-center gap-2 border-2 border-white/40 text-white hover:bg-white hover:text-ink px-8 py-4 rounded-full font-bold transition-colors duration-300">
                View our fleet
              </Link>
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.18 }} className="mt-6">
              <StoreButtons dark testidPrefix="hero" />
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm font-medium text-white/80">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-light" /> Licensed & insured</span>
              <span className="flex items-center gap-2"><PoundSterling className="h-4 w-4 text-brand-light" /> Fixed fares</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-brand-light" /> 24/7 service</span>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-6 lg:pl-6">
            <div data-testid="hero-book-card" className="bg-white rounded-2xl p-7 md:p-8 shadow-[0_24px_50px_-12px_rgba(0,0,0,0.25)]">
              <div className="flex items-center gap-2 mb-1">
                <span className="h-2 w-2 rounded-full bg-brand animate-pulse" />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-brand">Instant online booking</span>
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-ink mb-2">Reserve your journey in under a minute</h3>
              <p className="text-ink/60 leading-relaxed mb-6 text-sm">
                Fixed fares, live pricing and instant confirmation through our secure online booker.
                Flight-tracked, meet & greet included on airport pickups.
              </p>

              <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                  { code: "LGW", label: "Gatwick", price: "£25" },
                  { code: "LHR", label: "Heathrow", price: "£45" },
                  { code: "LDN", label: "London", price: "£75" },
                ].map((f) => (
                  <div key={f.code} data-testid={`hero-fare-${f.code}`} className="rounded-xl bg-surface p-3 text-center">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-ink/40">{f.label}</p>
                    <p className="font-heading text-xl font-black text-ink mt-1">{f.price}</p>
                  </div>
                ))}
              </div>

              <a
                href={BOOKING_URL}
                data-testid="hero-book-online-btn"
                className="block w-full text-center py-4 h-auto bg-brand hover:bg-brand-light text-white rounded-full text-base font-bold transition-all duration-300 hover:shadow-[0_8px_24px_rgba(31,157,42,0.35)] active:scale-[0.98]"
              >
                Book online now
              </a>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="hero-call-btn"
                className="mt-3 flex items-center justify-center gap-2 w-full py-3.5 rounded-full border-2 border-ink/10 hover:border-ink text-ink font-bold transition-colors text-sm"
              >
                <Phone className="h-4 w-4 text-brand" strokeWidth={2.25} /> Or call {COMPANY.phone}
              </a>

              <div className="mt-5 flex items-center justify-center gap-4 text-[11px] text-ink/40 font-medium">
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand" strokeWidth={3} /> No payment now</span>
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand" strokeWidth={3} /> Free cancellation</span>
                <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand" strokeWidth={3} /> Fixed price</span>
              </div>
            </div>
            <div className="mt-4">
              <GatwickLiveWait variant="dark" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* AIRPORTS STRIP */}
      <section className="bg-ink py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-white/50 text-sm font-semibold uppercase tracking-[0.15em]">
          <span className="text-brand-light">We cover</span>
          {["Gatwick", "Heathrow", "Stansted", "Luton", "London City", "Southampton"].map((a) => (
            <span key={a} className="flex items-center gap-2"><Plane className="h-4 w-4" /> {a}</span>
          ))}
        </div>
      </section>

      {/* FEATURED BLOG BAND — freshest article as an inline hero */}
      {articles[0] && (
        <section data-testid="featured-blog-band" className="bg-surface py-8 md:py-10 border-b border-[#EAEBEA]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Link
              to={`/blog/${articles[0].slug}`}
              data-testid={`featured-blog-link-${articles[0].slug}`}
              className="group flex flex-col md:flex-row md:items-center gap-5 md:gap-8 bg-white rounded-2xl border border-[#EAEBEA] hover:border-brand/40 hover:shadow-[0_20px_45px_-24px_rgba(0,0,0,0.25)] p-4 md:p-5 transition-all duration-300"
            >
              <div className="relative shrink-0 h-40 md:h-28 md:w-44 rounded-xl overflow-hidden">
                <img src={articles[0].image} alt={articles[0].title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[600ms]" />
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-brand text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                  </span>
                  Fresh
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-ink/40 mb-2">
                  <span className="text-brand">{articles[0].category}</span>
                  <span>·</span>
                  <span>{articles[0].read_time}</span>
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-ink leading-tight group-hover:text-brand transition-colors line-clamp-2">
                  {articles[0].title}
                </h3>
                <p className="mt-2 text-sm text-ink/60 leading-relaxed line-clamp-2 md:line-clamp-1">{articles[0].excerpt}</p>
              </div>
              <span className="hidden md:inline-flex items-center gap-2 shrink-0 text-brand font-semibold text-sm group-hover:gap-3 transition-all">
                Read the guide <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </div>
        </section>
      )}

      {/* CINEMATIC STORY SHOWCASE */}
      <StoryShowcase />

      {/* FIXED-FARE STRIP */}
      <section className="bg-surface py-16 md:py-20" data-testid="fare-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-10">
            <Eyebrow>Fixed airport fares</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-ink">Airport transfers designed around your schedule</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { name: "Gatwick Airport", code: "LGW", price: "£25", note: "~10 min from Crawley" },
              { name: "Heathrow Airport", code: "LHR", price: "£45", note: "~50 min via M23/M25" },
              { name: "Central London", code: "LDN", price: "£75", note: "~60 min door to door" },
            ].map((f, i) => (
              <motion.div key={f.code} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} data-testid={`fare-card-${i}`} className="group rounded-2xl bg-white border border-[#EAEBEA] p-6 hover:border-brand/40 hover:shadow-[0_20px_45px_-24px_rgba(0,0,0,0.25)] transition-all">
                <div className="flex items-center justify-between mb-4">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand/10 text-brand"><Plane className="h-5 w-5" /></span>
                  <span className="text-xs font-bold uppercase tracking-widest text-ink/40">{f.code}</span>
                </div>
                <h3 className="font-heading text-lg font-bold text-ink">{f.name}</h3>
                <p className="text-sm text-ink/50 mb-4">{f.note}</p>
                <div className="flex items-end gap-1">
                  <span className="text-xs font-semibold text-ink/50 mb-1">from</span>
                  <span className="font-heading text-3xl font-black text-brand">{f.price}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-16">
            <Eyebrow>How it works</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-ink">Luxury travel starts with one simple booking</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-10 lg:gap-16">
            {HOW_IT_WORKS.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }} data-testid={`step-${i}`} className="relative">
                <div className="flex items-center gap-4 mb-5">
                  <span className="font-heading text-5xl font-black text-surface">{s.step}</span>
                  <span className="h-px flex-1 bg-[#EAEBEA]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-brand" />
                </div>
                <h3 className="font-heading text-xl font-bold text-ink mb-2">{s.title}</h3>
                <p className="text-ink/60 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MOBILE APP SHOWCASE — editorial alternating bands */}
      <AppShowcase />

      {/* WATCH OUR FILM */}
      <FilmSection />

      {/* FLEET SHOWCASE — DARK */}
      <section className="bg-ink py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <Eyebrow dark>The fleet</Eyebrow>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white">Luxury vehicles for every journey</h2>
            </div>
            <Link to="/fleet" className="group inline-flex items-center gap-2 text-white font-semibold hover:text-brand-light transition-colors">
              Browse our fleet <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
          <FleetCarousel dark />
        </div>
      </section>

      {/* SERVICES */}
      <ServicesShowcase />

      {/* STATS BAND */}
      <section className="bg-ink py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <motion.div key={s.label} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="text-center">
              <p className="font-heading text-4xl md:text-5xl font-black text-brand-light tracking-tight">{s.value}</p>
              <p className="mt-2 text-sm text-white/50 uppercase tracking-wider font-medium">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ACCREDITATIONS */}
      <section className="bg-white py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <Eyebrow>Accredited & certified</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-ink">Trusted by travellers who value reliability</h2>
            <p className="mt-4 text-ink/60 leading-relaxed">We hold recognised ISO certifications and operate as a fully licensed, insured private-hire company with enhanced-DBS-checked drivers.</p>
          </motion.div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {ACCREDITATIONS.map((a, i) => (
              <motion.div key={a.code} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 3) * 0.08 }} data-testid={`accreditation-${i}`} className="flex items-start gap-5 bg-surface rounded-2xl p-6">
                <div className="grid place-items-center h-16 w-16 shrink-0 rounded-2xl bg-ink text-white">
                  <BadgeCheck className="h-8 w-8 text-brand-light" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="font-heading text-xl font-extrabold text-ink leading-none">{a.code}</p>
                  <p className="text-sm font-semibold text-brand mt-1">{a.label}</p>
                  <p className="text-sm text-ink/55 mt-1.5 leading-relaxed">{a.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRESTIGIOUS PATRONS — dark 3D perspective conveyor */}
      <section data-testid="prestigious-patrons" className="relative bg-ink py-24 md:py-32 overflow-hidden">
        {/* ambient green glow + grid */}
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-[420px] w-[820px] rounded-full bg-brand/20 blur-[130px]" />
          <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:56px_56px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-16">
            <Eyebrow dark>Prestigious patrons</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white">Trusted by leading organisations</h2>
            <p className="mt-4 text-white/55 leading-relaxed">From the NHS and local councils to global businesses, respected institutions across the South East rely on Clocktower Cars for their transport.</p>
          </motion.div>
        </div>

        <div className="patron-perspective relative">
          <div className="patron-stage space-y-6">
            {/* back layer — smaller, dimmer, drifts right */}
            <div className="flex gap-6 w-max animate-conveyor-rev patron-track opacity-55 scale-[0.86] blur-[0.5px]">
              {[...PATRONS.slice(8), ...PATRONS.slice(8)].map((p, i) => (
                <div
                  key={`pback-${p.name}-${i}`}
                  data-testid={`patron-row2-${i}`}
                  title={p.name}
                  className="shrink-0 h-24 w-44 grid place-items-center rounded-2xl bg-white/90 ring-1 ring-white/10 px-6"
                >
                  <img src={p.logo} alt={p.name} draggable="false" className="max-h-12 max-w-full object-contain select-none grayscale opacity-80" />
                </div>
              ))}
            </div>
            {/* front layer — sharp, drifts left, glows green on hover */}
            <div className="flex gap-7 w-max animate-conveyor patron-track">
              {[...PATRONS.slice(0, 8), ...PATRONS.slice(0, 8)].map((p, i) => (
                <div
                  key={`pfront-${p.name}-${i}`}
                  data-testid={`patron-${i}`}
                  title={p.name}
                  className="group shrink-0 h-28 w-52 grid place-items-center rounded-2xl bg-white ring-1 ring-white/10 px-7 shadow-lg transition-all duration-300 hover:ring-2 hover:ring-brand-light hover:shadow-[0_0_40px_-4px_rgba(31,157,42,0.6)] hover:-translate-y-1"
                >
                  <img src={p.logo} alt={p.name} draggable="false" className="max-h-14 max-w-full object-contain select-none grayscale opacity-80 transition-all duration-300 group-hover:grayscale-0 group-hover:opacity-100" />
                </div>
              ))}
            </div>
          </div>
          {/* edge fades */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 md:w-40 bg-gradient-to-r from-ink to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 md:w-40 bg-gradient-to-l from-ink to-transparent z-10" />
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-surface py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeUp} className="relative">
            <img src="/images/whychoose.jpg" alt="Professional chauffeur beside a black Mercedes S-Class outside a UK office" className="rounded-2xl w-full object-cover h-[440px] shadow-[0_24px_50px_-12px_rgba(0,0,0,0.2)]" />
            <div className="absolute -bottom-6 -right-2 sm:right-6 bg-white rounded-2xl shadow-xl p-5 flex items-center gap-4">
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand text-white"><ShieldCheck className="h-6 w-6" /></span>
              <div><p className="font-heading text-lg font-bold text-ink leading-none">DBS-checked</p><p className="text-sm text-ink/50">Professional chauffeurs</p></div>
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
            <Eyebrow>Why Clocktower</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-ink mb-6">A professional private hire company at the heart of Crawley</h2>
            <p className="text-ink/60 leading-relaxed mb-8">
              Based at Churchill Court, RH10 9KU, we&apos;re deeply embedded in local life — serving Furnace Green, Langley Green, Maidenbower, Broadfield, Copthorne and nearby towns. Our DBS-checked drivers know every route to Gatwick and across Surrey, with immaculate Mercedes vehicles, punctual pick-ups and clear, fixed pricing.
            </p>
            <ul className="grid sm:grid-cols-2 gap-4">
              {WHY_CHOOSE.map((w) => (
                <li key={w.title} className="flex items-center gap-3 text-ink/80 font-medium">
                  <span className="grid place-items-center h-6 w-6 rounded-full bg-brand/10 text-brand shrink-0"><Check className="h-4 w-4" strokeWidth={3} /></span>
                  {w.title}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* COVERAGE / AREAS */}
      <section className="bg-white py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <Eyebrow>Where we serve</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-ink">Where can we take you today?</h2>
          </motion.div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {AREAS.map((a, i) => (
              <motion.div key={a.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 6) * 0.04 }}>
                <Link to={`/areas/${a.slug}`} data-testid={`area-card-${a.slug}`} className="group flex items-center justify-between bg-white border border-[#EAEBEA] rounded-xl px-5 py-4 hover:border-ink transition-colors duration-300">
                  <span className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-brand" strokeWidth={2} />
                    <span className="font-semibold text-ink">{a.name}</span>
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-ink/30 group-hover:text-ink group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-surface py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <Eyebrow>Reviews</Eyebrow>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-ink">Every journey earns our reputation</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex text-brand">{[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-brand" />)}</span>
              <span className="font-heading text-xl font-bold text-ink">4.9<span className="text-ink/40 text-base font-medium">/5</span></span>
            </div>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={t.name} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} data-testid={`testimonial-${i}`} className="bg-white border border-[#EAEBEA] rounded-2xl p-7 flex flex-col gap-4">
                <div className="flex gap-0.5 text-brand">{[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4 fill-brand" />)}</div>
                <p className="text-ink/70 leading-relaxed flex-1">“{t.text}”</p>
                <div className="flex items-center gap-3 pt-2 border-t border-[#F0F0F0]">
                  <span className="grid place-items-center h-10 w-10 rounded-full bg-ink text-white font-heading font-bold text-sm">{t.name.charAt(0)}</span>
                  <div>
                    <p className="text-ink font-semibold text-sm">{t.name}</p>
                    <p className="text-xs text-ink/40">{t.area}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST ARTICLES */}
      {articles.length > 0 && (
      <section className="bg-white py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <Eyebrow>Blog & articles</Eyebrow>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-ink">Travel tips & insights</h2>
            </div>
            <Link to="/blog" className="group inline-flex items-center gap-2 text-ink font-semibold hover:text-brand transition-colors">
              View all <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-3">
            {articles.slice(0, 3).map((a, i) => (
              <motion.div key={a.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}>
                <Link to={`/blog/${a.slug}`} data-testid={`home-article-${a.slug}`} className="group block bg-white border border-[#EAEBEA] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.12)] transition-all duration-300 h-full">
                  <div className="h-52 overflow-hidden"><img src={a.image} alt={a.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[600ms]" /></div>
                  <div className="p-6">
                    <span className="text-xs bg-surface text-ink/70 font-semibold px-2.5 py-1 rounded-full">{a.category}</span>
                    <h3 className="font-heading text-lg font-bold text-ink mt-3 leading-snug group-hover:text-brand transition-colors">{a.title}</h3>
                    <p className="mt-2 text-sm text-ink/60 leading-relaxed line-clamp-2">{a.excerpt}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* FAQ */}
      <section id="faq" className="bg-white py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-12 text-center">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-ink">Questions, answered</h2>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
            <Accordion type="single" collapsible className="w-full">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`} className="border-b border-[#EAEBEA]">
                  <AccordionTrigger className="text-left font-heading text-lg font-semibold text-ink hover:text-brand hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-ink/60 leading-relaxed text-base pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.h2 {...fadeUp} className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-white">Experience airport travel the way it should be.</motion.h2>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-5 text-lg text-white/60 max-w-xl mx-auto">
            Luxury vehicles, professional chauffeurs, punctual service and complete peace of mind from the moment you book until you reach your destination.
          </motion.p>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="mt-9 flex flex-wrap justify-center gap-4">
            <a href={BOOKING_URL} className="bg-brand hover:bg-brand-light text-white px-9 py-4 rounded-full font-bold transition-all duration-300 hover:shadow-[0_8px_24px_rgba(31,157,42,0.35)] active:scale-95">Book your airport transfer</a>
            <Link to="/contact" className="border-2 border-white/25 hover:border-white text-white px-9 py-4 rounded-full font-bold transition-colors">Get a free quote</Link>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-8 flex justify-center">
            <StoreButtons dark testidPrefix="cta" />
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
